<template>
  <WithBackdrop
    :backdrop="data?.fields?.backdrop"
    :class="[$style.config, $style.wrap]"
  >
    <div :class="$style.inner">
      <header :class="$style.header">
        <BaseHeading size="h4" as="h2" :class="$style.heading">
          {{ data?.fields?.header }}
        </BaseHeading>
        <MarkedParser
          :md="data?.fields?.copy"
          :class="['u-fs-p1', $style.copy]"
        />
        <CallToAction
          v-for="cta in data.fields.ctas"
          :key="cta?.sys?.id"
          :class="$style.cta"
          :cta="cta"
        />
      </header>
      <div ref="swiperRef" :class="['swiper', $style.articles]">
        <div class="swiper-wrapper">
          <div
            v-for="result in computedArticles"
            :key="result?.sys?.id"
            class="swiper-slide"
          >
            <NuxtLinkLocale
              :to="`news/${result?.fields?.path}`"
              target="_blank"
            >
              <div :class="$style.article">
                <AspectRatio ratio="545x378">
                  <BasePicture
                    :src="`${result?.fields?.featuredImage?.fields?.file?.url}?fit=thumb&q=90&w=545`"
                    alt=""
                  />
                </AspectRatio>
                <div :class="$style.articleInfoWrap">
                  <div :class="$style.articleInfo">
                    <div :class="[$style.meta, 'u-type-p3']">
                      <span
                        v-if="
                          !result?.fields?.hidePublishedDateTime &&
                          result?.fields?.publishedDateTime
                        "
                      >
                        {{
                          format(
                            new Date(result.fields.publishedDateTime),
                            'LLL d, yyyy',
                          )
                        }}
                      </span>
                    </div>
                    <BaseHeading as="h3" size="h6" :class="$style.title">
                      {{ result?.fields?.title }}
                    </BaseHeading>
                  </div>
                </div>
              </div>
            </NuxtLinkLocale>
          </div>
        </div>
        <div :class="$style.buttons">
          <button
            :class="[$style.button, $style.prev]"
            aria-label="previous slide"
            @click="prevSlide"
          >
            <IconCarouselArrowLeft :class="$style.icon" />
          </button>
          <button
            :class="[$style.button, $style.next]"
            aria-label="next slide"
            @click="nextSlide"
          >
            <IconCarouselArrowRight :class="$style.icon" />
          </button>
        </div>
      </div>
    </div>
  </WithBackdrop>
</template>

<script setup lang="ts">
import { format } from 'date-fns'
import type { Swiper } from 'swiper'
import swiper from 'swiper'

import type { TypeContentBlockWithoutUnresolvableLinksResponse } from '~/types/contentful'

import 'swiper/css'

const { $visibility, $userAccessCheck } = useNuxtApp()
const { highestUserLevel } = storeToRefs(useUserStore())

const props = defineProps<{
  data: TypeContentBlockWithoutUnresolvableLinksResponse
}>()

const swiperRef = ref<(HTMLDivElement & { swiper: Swiper }) | null>(null)

const prevSlide = () => {
  swiperRef.value?.swiper.slidePrev()
}
const nextSlide = () => {
  swiperRef.value?.swiper.slideNext()
}

const computedArticles = computed(() => {
  const articles = props.data?.fields?.references?.filter?.(instanceOfArticle)

  const filtered = articles?.filter((article) => {
    const accessLevel = article.fields?.accessLevel
    const isVisible = $visibility(article.fields?.visibility)
    const hasAccess = $userAccessCheck(accessLevel, highestUserLevel.value)

    return isVisible && hasAccess
  })

  return filtered
})

onMounted(() => {
  if (swiperRef.value) {
    new swiper(swiperRef.value, {
      slidesPerView: 1.25,
      slidesPerGroup: 1,
      spaceBetween: 15,
      breakpoints: {
        576: {
          slidesPerView: 1.5,
        },
        768: {
          slidesPerView: 2.5,
        },
        1024: {
          slidesPerView: 1.5,
        },
        1440: {
          slidesPerView: 2.5,
          spaceBetween: 20,
        },
      },
    })
  }
})
</script>

<style lang="scss" module>
.config {
  @include breakpoint-variables(
    '--gb-wrap-padding',
    (
      base: 45px 0,
      md: 60px 0,
      lg: 90px 0,
    )
  );

  @include breakpoint-variables(
    '--gb-article-info-padding',
    (
      base: 15px 20px,
      md: 20px 30px,
    )
  );
}

.wrap {
  padding: var(--gb-wrap-padding);
  padding-left: 20px;
}

.inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 45px;
  max-width: 1760px;
  margin-left: auto;

  @include media('md') {
    flex-direction: row;
    gap: 60px;
  }
}

.header {
  text-align: center;
  padding-right: 20px;
  max-width: 800px;

  @include media('md') {
    text-align: left;
    padding-right: 0;
    max-width: 450px;
  }
}

.heading {
  margin-bottom: 10px;
}

.copy {
  margin-bottom: 15px;

  @include media('md') {
    margin-bottom: 30px;
  }
}

.articles {
  width: 100%;
}

.buttons {
  display: flex;
  gap: 10px;
  margin-top: 15px;

  @include media('xs') {
    gap: 15px;
    margin-top: 20px;
  }
}

.button {
  z-index: 1;
  background: transparent;
  border: 0;
  padding: 0;
  color: var(--purple);

  display: flex;
  align-items: center;
  justify-content: center;
  width: min-content;
  height: min-content;

  &:hover {
    color: var(--purple-dark);
  }
}

.icon {
  height: 12px;
  width: 24px;

  @include media('xs') {
    height: 17px;
    width: 34px;
  }
}

.meta {
  text-transform: uppercase;
  margin-bottom: 5px;
}

.title {
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-bottom: 3px;
}

.meta,
.title {
  color: var(--white);
}

.article {
  position: relative;
}

.articleInfoWrap {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  display: flex;
  padding: var(--gb-article-info-padding);
  background: linear-gradient(to top, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0) 60%);
}

.articleInfo {
  margin-top: auto;
}
</style>
